import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useScenarios } from '../hooks/useScenarios';
import { ScenarioTable } from '../components/ScenarioTable';
import { PermissionGate } from '../../admin/components/PermissionGate';
import { Permissions } from '../../admin/hooks/usePermissions';
import { showError, showToast } from '../../../utils/alerts';
import { ImportScenarioJsonModal, downloadScenarioTemplateJson } from '../components/ImportScenarioJsonModal';
import { AssignLearnersModal } from '../../admin/components/AssignLearnersModal';
import { usePermissions } from '../../admin/hooks/usePermissions';
import { CreateScenarioDto } from '../types';
import { AdminPageHeader } from '../../admin/components/AdminPageHeader';
import { useTranslation } from '../../../i18n';

export const ScenarioListPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { canAssign } = usePermissions();
  const {
    scenarios,
    loading,
    error,
    page,
    setPage,
    limit,
    setLimit,
    search,
    setSearch,
    status,
    setStatus,
    category,
    setCategory,
    difficulty,
    setDifficulty,
    total,
    totalPages,
    createScenario,
    publishScenario,
    unpublishScenario,
    duplicateScenario,
    deleteScenario,
  } = useScenarios();
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importedScenario, setImportedScenario] = useState<{ id: string; title: string; tenantId?: string } | null>(null);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);

  const totalCount = total || scenarios.length;
  const publishedCount = scenarios.filter((s) => s.status === 'PUBLISHED').length;
  const draftCount = scenarios.filter((s) => s.status === 'DRAFT').length;

  const handleImportScenario = async (data: CreateScenarioDto) => {
    setImporting(true);
    try {
      const res = await createScenario(data);
      if (res.success && res.data) {
        setIsImportModalOpen(false);
        setImportedScenario({ id: res.data.id, title: res.data.title, tenantId: res.data.tenantId });
        showToast('Scenario imported! Assign learners to publish this scenario.', 'info');
        setIsAssignModalOpen(true);
      } else {
        showError('Import Failed', (res.error as any)?.message || String(res.error || 'Failed to import scenario.'));
      }
    } catch (err: any) {
      showError('Import Error', err.message || 'An error occurred during scenario import.');
    } finally {
      setImporting(false);
    }
  };

  const handleAssignCompleted = async (count: number) => {
    if (!importedScenario) return;
    setIsAssignModalOpen(false);
    try {
      const pubRes = await publishScenario(importedScenario.id);
      if (pubRes.success) {
        showToast(`Assigned to ${count} learners & published scenario successfully!`, 'success');
      } else {
        showToast(`Assigned to ${count} learners. (Draft mode preserved)`, 'info');
      }
      navigate(`/scenarios/${importedScenario.id}`);
    } catch {
      navigate(`/scenarios/${importedScenario.id}`);
    }
  };

  const handleAssignModalClose = () => {
    setIsAssignModalOpen(false);
    if (importedScenario) {
      showToast('Scenario imported as Draft. Assign learners anytime to publish.', 'info');
      navigate(`/scenarios/${importedScenario.id}`);
    }
  };

  return (
    <div className="scenarios-page" style={{ display: 'grid', gap: '20px' }}>
      <AdminPageHeader
        breadcrumbs={[
          { label: 'Admin', path: '/admin' },
          { label: 'Scenario & Case Studio' },
        ]}
        icon="🎬"
        title="Scenario & Simulation Studio"
        badge="Simulation Authoring"
        description="Design, configure, and publish conversational AI simulation cases, personas, behavioral guardrails, and evaluation rubrics."
        actions={
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={downloadScenarioTemplateJson}
              className="admin-btn admin-btn--secondary"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                height: '38px',
                padding: '0 14px',
                background: 'rgba(255, 255, 255, 0.1)',
                color: '#ffffff',
                border: '1px solid rgba(255, 255, 255, 0.25)',
                borderRadius: '8px',
                fontSize: '13px',
                fontWeight: 600,
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                transition: 'all 0.15s ease',
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
              {t('scenarios.download_template', undefined, 'Download Template')}
            </button>

            <PermissionGate permission={Permissions.SCENARIO_CREATE_EDIT}>
              <button
                type="button"
                onClick={() => setIsImportModalOpen(true)}
                className="admin-btn admin-btn--secondary"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  height: '38px',
                  padding: '0 14px',
                  background: 'rgba(255, 255, 255, 0.15)',
                  color: '#93c5fd',
                  border: '1px solid rgba(147, 197, 253, 0.35)',
                  borderRadius: '8px',
                  fontSize: '13px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  transition: 'all 0.15s ease',
                }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="17 8 12 3 7 8" />
                  <line x1="12" y1="3" x2="12" y2="15" />
                </svg>
                {t('scenarios.import_json', undefined, 'Import JSON')}
              </button>

              <button
                type="button"
                onClick={() => navigate('/scenarios/new')}
                className="admin-btn admin-btn--primary"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  height: '38px',
                  padding: '0 16px',
                  background: '#2563eb',
                  color: '#fff',
                  borderRadius: '8px',
                  fontSize: '13.5px',
                  fontWeight: 700,
                  border: 'none',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  boxShadow: '0 4px 14px rgba(37, 99, 235, 0.4)',
                  transition: 'all 0.15s ease',
                }}
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
                {t('scenarios.create_scenario', undefined, 'Create Scenario')}
              </button>
            </PermissionGate>
          </div>
        }
      />

      <ImportScenarioJsonModal
        isOpen={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
        onImport={handleImportScenario}
        isSubmitting={importing}
      />

      {/* Metrics Row */}
      <div className="admin-stats-grid">
        <div className="admin-stat-card">
          <div className="admin-stat-card__icon" style={{ background: 'var(--blue-soft)', color: 'var(--blue)' }}>📚</div>
          <div className="admin-stat-card__content">
            <span className="admin-stat-card__label">{t('scenarios.stats.total', undefined, 'Total Scenarios')}</span>
            <span className="admin-stat-card__value">{totalCount}</span>
            <span className="admin-stat-card__subtitle">{t('scenarios.stats.total_sub', undefined, 'Authored simulation cases')}</span>
          </div>
        </div>

        <div className="admin-stat-card">
          <div className="admin-stat-card__icon" style={{ background: 'var(--ok-soft)', color: 'var(--ok)' }}>✅</div>
          <div className="admin-stat-card__content">
            <span className="admin-stat-card__label">{t('scenarios.stats.published', undefined, 'Published & Active')}</span>
            <span className="admin-stat-card__value" style={{ color: 'var(--ok)' }}>{publishedCount}</span>
            <span className="admin-stat-card__subtitle">{t('scenarios.stats.published_sub', undefined, 'Live for learner practice')}</span>
          </div>
        </div>

        <div className="admin-stat-card">
          <div className="admin-stat-card__icon" style={{ background: 'var(--warn-soft)', color: 'var(--warn)' }}>📝</div>
          <div className="admin-stat-card__content">
            <span className="admin-stat-card__label">{t('scenarios.stats.draft', undefined, 'In Draft Authoring')}</span>
            <span className="admin-stat-card__value" style={{ color: 'var(--warn)' }}>{draftCount}</span>
            <span className="admin-stat-card__subtitle">{t('scenarios.stats.draft_sub', undefined, 'Configuration in progress')}</span>
          </div>
        </div>
      </div>

      {error && (
        <div style={{ padding: '16px 18px', background: 'var(--bad-soft)', border: '1px solid #F1C7C7', borderRadius: '12px', color: 'var(--bad)', fontSize: '13.5px' }}>
          {error}
        </div>
      )}

      {/* Scenarios Table with Pagination */}
      <div style={{ background: 'var(--white)', border: '1px solid var(--line)', borderRadius: '12px', overflow: 'hidden' }}>
        <ScenarioTable
          scenarios={scenarios}
          loading={loading}
          page={page}
          limit={limit}
          total={total}
          totalPages={totalPages}
          search={search}
          onSearchChange={(s) => {
            setSearch(s);
            setPage(1);
          }}
          statusFilter={status}
          onStatusFilterChange={(st) => {
            setStatus(st);
            setPage(1);
          }}
          categoryFilter={category}
          onCategoryFilterChange={(cat) => {
            setCategory(cat);
            setPage(1);
          }}
          difficultyFilter={difficulty}
          onDifficultyFilterChange={(diff) => {
            setDifficulty(diff);
            setPage(1);
          }}
          onPageChange={(p) => setPage(p)}
          onLimitChange={(l) => {
            setLimit(l);
            setPage(1);
          }}
          onPublish={async (id) => {
            const res = await publishScenario(id);
            if (res.success) {
              showToast('Scenario published successfully', 'success');
            } else {
              showError('Publish Failed', res.error || 'Failed to publish scenario.');
            }
          }}
          onUnpublish={async (id) => {
            const res = await unpublishScenario(id);
            if (res.success) {
              showToast('Scenario reverted to draft', 'info');
            } else {
              showError('Unpublish Failed', res.error || 'Failed to unpublish scenario.');
            }
          }}
          onDuplicate={async (id) => {
            const res = await duplicateScenario(id);
            if (res.success && res.data) {
              showToast(`Duplicated "${res.data.title}" successfully`, 'success');
            } else {
              showError('Duplication Failed', res.error || 'Failed to duplicate scenario.');
            }
          }}
          onArchive={async (id) => {
            const res = await deleteScenario(id);
            if (res.success) {
              showToast('Scenario archived successfully', 'success');
            } else {
              showError('Archive Failed', res.error || 'Failed to archive scenario.');
            }
          }}
        />

        {/* Assign Learners Modal after JSON import */}
        {canAssign && importedScenario && (
          <AssignLearnersModal
            simulationId={importedScenario.id}
            simulationTitle={importedScenario.title}
            tenantId={importedScenario.tenantId}
            isOpen={isAssignModalOpen}
            onClose={handleAssignModalClose}
            onAssigned={handleAssignCompleted}
          />
        )}
      </div>
    </div>
  );
};
